<template>
  <template v-if="internal_task">
    <div
      v-if="!initialized"
      :data-tint="statusTint"
      class="task-gradient squircle-md flex cursor-pointer items-center justify-center rounded px-1 py-0.5"
      @mouseover="initialized = true"
    >
      <FontAwesomeIcon
        v-if="internal_task.label === 'icon_status'"
        class="font-bold"
        :icon="['fas', mainTitle]"
        size="lg"
      />
      <FontAwesomeIcon
        v-else-if="internal_task.tooltip"
        class="font-bold"
        :icon="['fas', 'circle-info']"
        size="lg"
      />
      <div v-else class="@container/task w-full">
        <div class="@9xs/task:hidden block font-bold">
          <FontAwesomeIcon :icon="['fas', iconStatus]" />
        </div>
        <div
          class="capitalize-first @9xs/task:block hidden truncate font-semibold whitespace-nowrap"
        >
          {{ mainTitle === '#######' ? '&nbsp;' : mainTitle }}
        </div>
      </div>
    </div>
    <Popover v-else class="">
      <Float
        portal
        auto-placement
        :arrow="5"
        :offset="10"
        enter-active-class="transition-opacity duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <PopoverButton
          :data-tint="statusTint"
          as="div"
          class="task-gradient squircle-md flex cursor-pointer items-center justify-center rounded px-1 py-0.5"
        >
          <FontAwesomeIcon
            v-if="internal_task.label === 'icon_status'"
            class="font-bold"
            :icon="['fas', mainTitle]"
            size="lg"
          />
          <FontAwesomeIcon
            v-else-if="internal_task.tooltip"
            class="font-bold"
            :icon="['fas', 'circle-info']"
            size="lg"
          />
          <div v-else class="@container/task w-full">
            <div class="@9xs/task:hidden block font-bold">
              <FontAwesomeIcon :icon="['fas', iconStatus]" />
            </div>
            <div
              class="capitalize-first @9xs/task:block hidden truncate font-semibold whitespace-nowrap"
            >
              {{ mainTitle === '#######' ? '&nbsp;' : mainTitle }}
            </div>
          </div>
        </PopoverButton>
        <PopoverPanel
          v-slot="{ close }"
          :class="[
            widthPanel,
            'z-10 flex flex-col overflow-hidden rounded-md border border-gray-300 shadow-xl'
          ]"
        >
          <FloatArrow
            class="absolute h-5 w-5 rotate-45 transform border border-gray-300 bg-white"
          />
          <div class="relative flex flex-col gap-2 bg-white p-2">
            <h1
              v-if="internal_task.tooltip"
              class="capitalize-first rounded bg-gray-100 px-2 text-left text-base font-bold"
            >
              <span>{{ information }}</span> :
              <div
                v-if="internal_task.label === '#######'"
                class="capitalize-first text-xs font-medium"
              >
                {{ $t('attributes.undefined') }}
              </div>
              <div
                v-else
                class="capitalize-first text-xs font-medium"
                v-html="internal_task.label"
              ></div>
            </h1>
            <div class="flex flex-col gap-2">
              <button
                v-for="status in task_statuses"
                :key="status.id"
                :data-tint="`task-status-${status.id}`"
                class="task-gradient relative rounded-md px-2 py-1 text-center"
                @click="
                  () => handleClickChangeStatus(parseInt(status.id), close)
                "
              >
                <span
                  v-if="internal_task.status === parseInt(status.id)"
                  class="absolute inset-y-0 left-2 flex items-center"
                >
                  <span class="h-2 w-2 rounded-full bg-white">&nbsp;</span>
                </span>
                <span>{{ status.name }}</span>
              </button>
            </div>
            <div
              v-if="internal_task.executors && internal_task.executors.length"
            >
              <template
                v-for="executor in internal_task.executors"
                :key="executor"
              >
                <AvatarComponent :user_id="executor" />
              </template>
            </div>
          </div>
        </PopoverPanel>
      </Float>
    </Popover>
  </template>
</template>

<script>
import {
  Popover,
  PopoverButton,
  PopoverOverlay,
  PopoverPanel
} from '@headlessui/vue'
import { mapActions, mapGetters } from 'vuex'
import AvatarComponent from '@c/AvatarComponent'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  Float,
  FloatArrow,
  FloatContent,
  FloatReference
} from '@headlessui-float/vue'
import { TaskStatus } from '@/assets/enums'

export default {
  name: 'TaskComponent',
  components: {
    PopoverOverlay,
    FloatContent,
    FloatReference,
    FloatArrow,
    Float,
    FontAwesomeIcon,
    AvatarComponent,
    Popover,
    PopoverButton,
    PopoverPanel
  },
  props: {
    task: {
      type: Object,
      required: true
    }
  },
  emits: ['update:task'],
  data() {
    return {
      websocket_channel: null,
      internal_task: null,
      initialized: false
    }
  },
  computed: {
    ...mapGetters({
      task_statuses: 'metadata/getMetadataTaskStatuses',
      initialMetadata: 'metadata/getInitialMetadata',
      user: 'auth/getUser'
    }),
    localTask: {
      get() {
        return this.task
      },
      set(value) {
        this.$emit('update:task', value)
      }
    },
    statusTint() {
      return this.task.status !== undefined
        ? `task-status-${this.task.status}`
        : 'independence'
    },
    widthPanel() {
      return this.internal_task?.tooltip && this.mainTitle !== '#######'
        ? 'w-82'
        : 'w-48'
    },
    information() {
      return this.initialMetadata?.enums?.task_informations.find(
        (info) => info.id === this.internal_task.info
      )?.name
    },
    iconStatus() {
      return this.task_statuses.find(
        (task_status) => task_status.id === this.internal_task.status
      )?.icon
    },
    mainTitle() {
      let taskStatus = this.task_statuses.find(
        ({ key }) => parseInt(key) === this.internal_task.status
      )
      if (this.internal_task.label === 'icon_status') {
        return taskStatus?.icon
      }
      if (typeof this.internal_task.main_list_info === 'string') {
        return this.internal_task[this.internal_task.main_list_info]
      }
      if (this.internal_task.label && this.internal_task.label?.length) {
        return this.internal_task?.label
      }
      if (taskStatus) {
        return taskStatus.name
      }
      return `task #${this.internal_task.id}`
    }
  },
  mounted() {
    this.internal_task = this.task
    if (this.$pusher) {
      this.websocket_channel = this.$pusher
        .subscribe(`task_dossier.${this.task.id}`)
        .bind('event', (data) => {
          if (this.user.id !== data.triggered_by.id) {
            if (data.event === 'task_dossier.updated') {
              this.processFetchTaskDossierByIdAction({
                id: this.task.id
              }).then((response) => {
                this.internal_task = response.task_dossier
              })
            }
          }
        })
      // .bind('notification', (data) => {
      //   console.log(data)
      // })
    }
  },
  unmounted() {
    this.websocket_channel?.unsubscribe()
  },
  methods: {
    ...mapActions({
      processUpdateTaskDashboardAction: 'taskDossier/updateTaskDashboard',
      processFetchTaskDossierByIdAction: 'taskDossier/fetchTaskDossierById'
    }),
    handleClickChangeStatus(newStatus, close) {
      this.internal_task.status = newStatus
      this.processUpdateTaskDashboardAction({
        id: this.task.id,
        task: this.task
      })
      if (typeof close === 'function') {
        close()
      }
    }
  }
}
</script>
